import React, { useState } from 'react';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Badge,
  Space,
  Typography,
  Breadcrumb,
  Tooltip,
  Switch,
  Divider,
  theme,
} from 'antd';
import {
  DashboardOutlined,
  ShopOutlined,
  UserOutlined,
  BarChartOutlined,
  CustomerServiceOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  FileTextOutlined,
  MoonOutlined,
  SunOutlined,
  SearchOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '@refinedev/core';
import { motion } from 'framer-motion';
import { useAuth } from '@/providers/auth-provider';
import { useTheme } from '@/providers/theme-provider';
import { Can } from '@/components/auth/permission-guard';
import type { MenuItem } from '@/types';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, hasPermission } = useAuth();
  const { themeMode, toggleTheme, isDark } = useTheme();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // Generate menu items based on user permissions
  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardOutlined />,
        path: '/dashboard',
      },
    ];

    // Partner management (for super_admin and partner_manager)
    if (hasPermission('manage_partners')) {
      items.push({
        key: 'partners',
        label: 'Partner Management',
        icon: <ShopOutlined />,
        children: [
          {
            key: 'partners-list',
            label: 'All Partners',
            path: '/partners',
          },
          {
            key: 'partners-onboarding',
            label: 'Onboarding',
            path: '/partners/onboarding',
          },
          {
            key: 'partners-pending',
            label: 'Pending Approval',
            path: '/partners/pending',
          },
        ],
      });
    }

    // User management (for super_admin)
    if (hasPermission('manage_users')) {
      items.push({
        key: 'users',
        label: 'User Management',
        icon: <TeamOutlined />,
        children: [
          {
            key: 'users-customers',
            label: 'Customers',
            path: '/users/customers',
          },
          {
            key: 'users-admins',
            label: 'Admin Users',
            path: '/users/admins',
          },
        ],
      });
    }

    // Staff management (for super_admin and partner_manager)
    if (hasPermission('manage_staff') || hasPermission('manage_users')) {
      items.push({
        key: 'staff',
        label: 'Staff Management',
        icon: <UserOutlined />,
        path: '/staff',
      });
    }

    // Booking monitoring
    items.push({
      key: 'bookings',
      label: 'Booking Monitor',
      icon: <FileTextOutlined />,
      path: '/bookings',
    });

    // Support tickets (for customer_support and super_admin)
    if (hasPermission('manage_tickets')) {
      items.push({
        key: 'support',
        label: 'Support',
        icon: <CustomerServiceOutlined />,
        children: [
          {
            key: 'support-tickets',
            label: 'Tickets',
            path: '/support/tickets',
          },
          {
            key: 'support-knowledge',
            label: 'Knowledge Base',
            path: '/support/knowledge',
          },
        ],
      });
    }

    // Analytics (for most roles)
    if (hasPermission('view_analytics')) {
      items.push({
        key: 'analytics',
        label: 'Analytics',
        icon: <BarChartOutlined />,
        children: [
          {
            key: 'analytics-overview',
            label: 'Overview',
            path: '/analytics',
          },
          {
            key: 'analytics-partners',
            label: 'Partner Performance',
            path: '/analytics/partners',
          },
          {
            key: 'analytics-revenue',
            label: 'Revenue',
            path: '/analytics/revenue',
          },
        ],
      });
    }

    // System settings (for super_admin)
    if (hasPermission('manage_system')) {
      items.push({
        key: 'settings',
        label: 'Settings',
        icon: <SettingOutlined />,
        children: [
          {
            key: 'settings-general',
            label: 'General',
            path: '/settings/general',
          },
          {
            key: 'settings-permissions',
            label: 'Permissions',
            path: '/settings/permissions',
          },
          {
            key: 'settings-audit',
            label: 'Audit Logs',
            path: '/settings/audit',
          },
        ],
      });
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Convert menu items to Ant Design format
  const getAntdMenuItems = (items: MenuItem[]) => {
    return items.map(item => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      children: item.children ? getAntdMenuItems(item.children) : undefined,
      onClick: item.path ? () => navigate(item.path!) : undefined,
    }));
  };

  // Get current selected menu key based on pathname
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/partners')) {
      if (path.includes('/onboarding')) return ['partners-onboarding'];
      if (path.includes('/pending')) return ['partners-pending'];
      return ['partners-list'];
    }
    if (path.startsWith('/users')) {
      if (path.includes('/admins')) return ['users-admins'];
      return ['users-customers'];
    }
    if (path.startsWith('/staff')) return ['staff'];
    if (path.startsWith('/bookings')) return ['bookings'];
    if (path.startsWith('/support')) {
      if (path.includes('/knowledge')) return ['support-knowledge'];
      return ['support-tickets'];
    }
    if (path.startsWith('/analytics')) {
      if (path.includes('/partners')) return ['analytics-partners'];
      if (path.includes('/revenue')) return ['analytics-revenue'];
      return ['analytics-overview'];
    }
    if (path.startsWith('/settings')) {
      if (path.includes('/permissions')) return ['settings-permissions'];
      if (path.includes('/audit')) return ['settings-audit'];
      return ['settings-general'];
    }
    return ['dashboard'];
  };

  // Get breadcrumb items
  const getBreadcrumbItems = () => {
    const path = location.pathname;
    const items = [{ title: 'Home' }];

    if (path.startsWith('/partners')) {
      items.push({ title: 'Partner Management' });
      if (path.includes('/onboarding')) items.push({ title: 'Onboarding' });
      else if (path.includes('/pending')) items.push({ title: 'Pending Approval' });
      else items.push({ title: 'All Partners' });
    } else if (path.startsWith('/users')) {
      items.push({ title: 'User Management' });
      if (path.includes('/admins')) items.push({ title: 'Admin Users' });
      else items.push({ title: 'Customers' });
    } else if (path.startsWith('/staff')) {
      items.push({ title: 'Staff Management' });
    } else if (path.startsWith('/bookings')) {
      items.push({ title: 'Booking Monitor' });
    } else if (path.startsWith('/support')) {
      items.push({ title: 'Support' });
      if (path.includes('/knowledge')) items.push({ title: 'Knowledge Base' });
      else items.push({ title: 'Tickets' });
    } else if (path.startsWith('/analytics')) {
      items.push({ title: 'Analytics' });
      if (path.includes('/partners')) items.push({ title: 'Partner Performance' });
      else if (path.includes('/revenue')) items.push({ title: 'Revenue' });
      else items.push({ title: 'Overview' });
    } else if (path.startsWith('/settings')) {
      items.push({ title: 'Settings' });
      if (path.includes('/permissions')) items.push({ title: 'Permissions' });
      else if (path.includes('/audit')) items.push({ title: 'Audit Logs' });
      else items.push({ title: 'General' });
    } else {
      items.push({ title: 'Dashboard' });
    }

    return items;
  };

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/account/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'theme',
      icon: isDark ? <SunOutlined /> : <MoonOutlined />,
      label: (
        <div className="flex items-center justify-between w-full">
          <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          <Switch
            size="small"
            checked={isDark}
            onChange={toggleTheme}
            checkedChildren={<MoonOutlined />}
            unCheckedChildren={<SunOutlined />}
          />
        </div>
      ),
      onClick: (e: any) => {
        e.preventDefault();
        toggleTheme();
      },
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => logout(),
    },
  ];

  const getRoleDisplayName = (role: string) => {
    const roleMap = {
      super_admin: 'Super Admin',
      partner_manager: 'Partner Manager',
      customer_support: 'Customer Support',
      analyst: 'Data Analyst',
    };
    return roleMap[role as keyof typeof roleMap] || role;
  };

  return (
    <Layout className="min-h-screen">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={250}
        className="modern-sidebar"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="p-4 text-center border-b border-gray-700">
          <Typography.Title
            level={collapsed ? 5 : 3}
            style={{ color: 'white', margin: 0 }}
          >
            {collapsed ? 'RESI' : 'RESI Admin'}
          </Typography.Title>
          {!collapsed && (
            <Text type="secondary" style={{ color: '#ccc', fontSize: '12px' }}>
              Restaurant Platform
            </Text>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKey()}
          items={getAntdMenuItems(menuItems)}
          style={{ border: 'none' }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'margin-left 0.2s' }}>
        <Header
          className={`sticky top-0 z-50 admin-glassmorphism border-b transition-all duration-300 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
          style={{
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 64,
          }}
        >
          <div className="flex items-center space-x-4">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="text-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                size="large"
              />
            </motion.div>
            
            {/* Global Search (placeholder for future implementation) */}
            <div className="hidden md:block">
              <Button
                type="text"
                icon={<SearchOutlined />}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span className="ml-1 text-sm">Search...</span>
              </Button>
            </div>
          </div>

          <Space size="middle" className="items-center">
            {/* Quick theme toggle */}
            <Tooltip title={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button
                  type="text"
                  icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                  onClick={toggleTheme}
                  className="text-lg text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
                />
              </motion.div>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications">
              <Badge count={5} size="small" className="cursor-pointer">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    type="text" 
                    icon={<BellOutlined />} 
                    className="text-lg text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
                  />
                </motion.div>
              </Badge>
            </Tooltip>

            {/* User Profile */}
            <Dropdown 
              menu={{ items: userMenuItems }} 
              placement="bottomRight"
              trigger={['click']}
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="cursor-pointer"
              >
                <Space className="px-2 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <Avatar 
                    icon={<UserOutlined />} 
                    className="bg-blue-500"
                    size="default"
                  />
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.role && getRoleDisplayName(user.role)}
                    </div>
                  </div>
                </Space>
              </motion.div>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ margin: '24px', minHeight: 'calc(100vh - 112px)' }}>
          <div style={{ marginBottom: '16px' }}>
            <Breadcrumb items={getBreadcrumbItems()} />
          </div>

          <div
            className="admin-glassmorphism"
            style={{
              padding: 24,
              minHeight: 360,
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};